import Alpine from 'alpinejs';
import axios from '../../config/axios.js';

function removeBgTool(cfg = {}) {
  return {
    file: null,
    sourcePreview: null,
    resultBlob: null,
    resultPreview: null,
    processing: false,
    exporting: false,
    dragging: false,
    progressText: '0%',
    progressStep: 0,
    loggedIn: false,
    canHd: false,
    eligMessage: 'Memeriksa akses HD…',
    hdMinLevel: cfg.hd_min_level || 7,
    mediumMax: cfg.medium_max_edge || 1200,
    hdMax: cfg.hd_max_edge || 4096,
    maxKb: cfg.max_upload_kb || 10240,

    async init() {
      this.loggedIn = !!Alpine.store('auth')?.isLoggedIn;
      await this.refreshEligibility();
    },

    async refreshEligibility() {
      try {
        const { data } = await axios.get('/api/v1/tools/remove-bg/eligibility');
        const e = data.data || {};
        this.loggedIn = !!e.logged_in;
        this.canHd = !!e.can_download_hd;
        this.eligMessage = e.message || '';
        this.hdMinLevel = e.hd_min_level || this.hdMinLevel;
        if (e.qualities?.medium?.max_edge) this.mediumMax = e.qualities.medium.max_edge;
        if (e.qualities?.hd?.max_edge) this.hdMax = e.qualities.hd.max_edge;
      } catch {
        this.eligMessage = `HD tersedia untuk Level XP ${this.hdMinLevel}.`;
        this.canHd = false;
      }
    },

    onDrop(e) {
      this.dragging = false;
      const f = e.dataTransfer?.files?.[0];
      if (f) this.setFile(f);
    },

    onFileSelect(e) {
      const f = e.target.files?.[0];
      if (f) this.setFile(f);
    },

    setFile(file) {
      if (!file.type.startsWith('image/')) {
        window.toast?.('File harus berupa gambar.', 'error');
        return;
      }
      if (file.size > this.maxKb * 1024) {
        window.toast?.(`Ukuran maks. ${Math.round(this.maxKb / 1024)} MB.`, 'error');
        return;
      }
      this.resetResult();
      this.file = file;
      this.sourcePreview = URL.createObjectURL(file);
      this.process();
    },

    resetResult() {
      this.resultBlob = null;
      if (this.resultPreview) URL.revokeObjectURL(this.resultPreview);
      this.resultPreview = null;
    },

    reset() {
      this.file = null;
      if (this.sourcePreview) URL.revokeObjectURL(this.sourcePreview);
      this.sourcePreview = null;
      this.resetResult();
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },

    setProgressStep(step) {
      const steps = [0, 25, 50, 75, 100];
      this.progressStep = steps[Math.min(step, steps.length - 1)] ?? 0;
      this.progressText = `${this.progressStep}%`;
    },

    async process() {
      if (!this.file || this.processing) return;
      this.processing = true;
      this.setProgressStep(0);
      this.resetResult();

      try {
        this.setProgressStep(1);
        const { removeBackground } = await import('@imgly/background-removal');
        this.setProgressStep(2);
        const blob = await removeBackground(this.file, {
          progress: (_key, current, total) => {
            if (total && current / total >= 0.75) this.setProgressStep(3);
            else if (total && current / total >= 0.5) this.setProgressStep(2);
          },
        });
        this.setProgressStep(4);
        this.resultBlob = blob;
        this.resultPreview = URL.createObjectURL(blob);
        await this.refreshEligibility();
      } catch (err) {
        console.error(err);
        window.toast?.(err?.message || 'Gagal menghapus background.', 'error');
      } finally {
        this.processing = false;
      }
    },

    async download(quality) {
      if (!this.resultBlob) return;

      if (quality === 'hd') {
        await this.refreshEligibility();
        if (!this.canHd) {
          if (!this.loggedIn) {
            window.toast?.('Login dulu untuk unduhan HD.', 'info');
            window.location.href = '/login?redirect=' + encodeURIComponent('/tools/remove-bg');
            return;
          }
          window.toast?.(this.eligMessage || 'HD hanya untuk Level XP 7.', 'error');
          return;
        }
      }

      this.exporting = true;
      try {
        const maxEdge = quality === 'hd' ? this.hdMax : this.mediumMax;
        const out = await this.resizeBlob(this.resultBlob, maxEdge);
        const a = document.createElement('a');
        a.href = URL.createObjectURL(out);
        a.download = `uspatih-remove-bg-${quality}.png`;
        a.click();
        URL.revokeObjectURL(a.href);
        window.toast?.(`Unduhan ${quality.toUpperCase()} siap.`, 'success');
      } catch (e) {
        window.toast?.(e?.message || 'Gagal mengunduh.', 'error');
      } finally {
        this.exporting = false;
      }
    },

    resizeBlob(blob, maxEdge) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(blob);
        img.onload = () => {
          let { width, height } = img;
          const scale = Math.min(1, maxEdge / Math.max(width, height));
          width = Math.round(width * scale);
          height = Math.round(height * scale);
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          URL.revokeObjectURL(url);
          canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error('Gagal encode PNG'))),
            'image/png',
          );
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Gagal membaca hasil'));
        };
        img.src = url;
      });
    },
  };
}

Alpine.data('removeBgTool', removeBgTool);
